import React from 'react';
import { 
  ArrowRight,
  PlayCircle,
  Mail,
  Download,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-[#1A365D] to-[#2A4A7F] text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                Datos Estratégicos para Empresas que Buscan Crecer
              </h1>
              <p className="text-xl text-gray-300">
                Convierto tus inversiones en datos en resultados de negocio tangibles para empresas mexicanas
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://calendly.com/your-calendly-link" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#27AE60] px-6 py-3 rounded-md hover:bg-[#219653] transition-colors flex items-center justify-center"
                  aria-label="Agendar diagnóstico gratuito"
                >
                  Agenda tu diagnóstico sin costo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <button 
                  className="border border-white px-6 py-3 rounded-md hover:bg-white/10 transition-colors flex items-center justify-center"
                  aria-label="Ver metodología"
                >
                  Conoce mi enfoque metodológico
                  <PlayCircle className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80"
                alt="Dashboard de análisis de datos mostrando gráficos y métricas de negocio"
                className="rounded-lg shadow-2xl"
                width="600"
                height="400"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1A365D] mb-12">Artículos Destacados</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <a 
              href="https://www.linkedin.com/pulse/estrategia-de-datos-construyendo-puentes-entre-y-campos-navarro-ik0me/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              aria-label="Leer artículo: Estrategia de Datos: Construyendo Puentes"
            >
              <h3 className="text-xl font-bold text-[#1A365D] mb-3">Estrategia de Datos: Construyendo Puentes</h3>
              <div className="flex items-center text-[#4A90E2]">
                <span>Leer más</span>
                <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>
            <a 
              href="https://www.linkedin.com/pulse/la-crisis-silenciosa-del-data-management-por-qu%C3%A9-tu-campos-navarro-gzoue/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              aria-label="Leer artículo: La Crisis Silenciosa del Data Management"
            >
              <h3 className="text-xl font-bold text-[#1A365D] mb-3">La Crisis Silenciosa del Data Management</h3>
              <div className="flex items-center text-[#4A90E2]">
                <span>Leer más</span>
                <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1A365D] mb-12">Episodios del Podcast</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <a 
              href="https://creators.spotify.com/pod/show/creativos/episodes/Impacto-d-elos-datos-y-la-tecnologa-en-los-negocios-e2v2l25"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-100"
              aria-label="Escuchar podcast: Impacto de los Datos y la Tecnología en los Negocios"
            >
              <h3 className="text-xl font-bold text-[#1A365D] mb-3">Impacto de los Datos y la Tecnología en los Negocios</h3>
              <div className="flex items-center text-[#4A90E2]">
                <span>Escuchar en Spotify</span>
                <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>
            <a 
              href="https://creators.spotify.com/pod/show/creativos/episodes/Impacto-de-los-datos-y-la-tecnologa-en-los-negocios-e2v2l25/a-abpoual"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-100"
              aria-label="Escuchar podcast: La Evolución de los Datos en la Era Digital"
            >
              <h3 className="text-xl font-bold text-[#1A365D] mb-3">La Evolución de los Datos en la Era Digital</h3>
              <div className="flex items-center text-[#4A90E2]">
                <span>Escuchar en Spotify</span>
                <ExternalLink className="ml-2 h-4 w-4" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A365D] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Tu Estrategia de Datos Puede Ser Tu Mayor Ventaja Competitiva
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            ¿Estás aprovechando todo el potencial de tus datos?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://calendly.com/your-calendly-link"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#27AE60] px-8 py-4 rounded-md hover:bg-[#219653] transition-colors inline-flex items-center justify-center"
              aria-label="Agendar sesión de diagnóstico gratuita"
            >
              <Mail className="mr-2 h-5 w-5" />
              Agenda tu sesión de diagnóstico gratuita
            </a>
            <Link 
              to="/recursos"
              className="border border-white px-8 py-4 rounded-md hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              aria-label="Ver recursos gratuitos"
            >
              <Download className="mr-2 h-5 w-5" />
              Descarga recursos gratuitos
            </Link>
          </div>
          <p className="mt-6 text-sm text-gray-400">
            Respuesta en menos de 24 horas, soluciones prácticas desde la primera llamada
          </p>
        </div>
      </section>
    </>
  );
};

export default HomePage;